// ========================================
// Local File Storage Service
// Stores data in memory backed by BrowserState
// ========================================

const FileStorage = {
    // Data store
    dataStore: {
        professors: [],
        formations: [],
        subjects: [],
        classes: [],
        messages: [],
        notifications: [],
        users: [],
        settings: {
            holidays: []
        },
        currentUser: null,
        professorDocs: [],
        payrollHistory: [],
        justificantes: [],
        calendarFilters: {},
        inventory: [],
        warehouseRequests: [],
        purchaseOrders: [],
        students: [],
        projects: [],
        leads: [],
        zones: [],
        studyBlocks: {}
    },

    // File mapping for each data type
    keyMapping: {
        'ecpv_professors': 'professors',
        'ecpv_formations': 'formations',
        'ecpv_subjects': 'subjects',
        'ecpv_classes': 'classes',
        'ecpv_messages': 'messages',
        'ecpv_notifications': 'notifications',
        'ecpv_users_db': 'users',
        'ecpv_global_settings': 'settings',
        'ecpv_professor_docs': 'professorDocs',
        'ecpv_payroll_history': 'payrollHistory',
        'ecpv_justificantes': 'justificantes',
        'ecpv_calendar_filters': 'calendarFilters',
        'ecpv_inventory': 'inventory',
        'ecpv_warehouse_requests': 'warehouseRequests',
        'ecpv_purchase_orders': 'purchaseOrders',
        'ecpv_current_user': 'currentUser',
        'ecpv_students': 'students',
        'ecpv_warehouse_projects': 'projects',
        'ecpv_leads': 'leads',
        'ecpv_floorplan_zones': 'zones',
        'ecpv_study_blocks': 'studyBlocks'
    },

    // Map internal paths to physical filenames in data/ folder
    fileMapping: {
        'professors': 'professors.json',
        'formations': 'formations.json',
        'subjects': 'subjects.json',
        'classes': 'classes.json',
        'messages': 'messages.json',
        'notifications': 'notifications.json',
        'users': 'users.json',
        'settings': 'settings.json',
        'professorDocs': 'professor-docs.json',
        'payrollHistory': 'payroll-history.json',
        'justificantes': 'justificantes.json',
        'calendarFilters': 'calendar-filters.json',
        'inventory': 'inventory.json',
        'warehouseRequests': 'warehouse-requests.json',
        'purchaseOrders': 'purchase-orders.json',
        'students': 'students.json',
        'projects': 'projects.json',
        'leads': 'leads.json',
        'zones': 'floorplan-zones.json',
        'studyBlocks': 'study-blocks.json'
    },

    /**
     * Initialize - Load data from BrowserState if available
     */
    async init() {
        console.log('🔄 Initializing FileStorage...');

        let localDataFound = false;

        // Try to load from BrowserState first
        for (const [key, path] of Object.entries(this.keyMapping)) {
            const localData = BrowserState.getItem(key);
            if (localData) {
                localDataFound = true;
                try {
                    // Try to parse as JSON first
                    const parsed = JSON.parse(localData);
                    this.setByPath(path, parsed);
                } catch (e) {
                    // If it fails, it might be a plain string; store it as-is
                    if (key === 'ecpv_current_user') {
                        // For these special keys, the value might be a plain string
                        this.setByPath(path, localData);
                    } else {
                        console.warn(`Failed to parse ${key}, skipping:`, e.message);
                    }
                }
            }
        }

        // Critical: If no data found (or always on startup to ensure sync), try to load from server
        // This ensures multi-browser consistency even on production
        // We always try to hydrate from server on startup now
        await this.loadAllFromServer();

        console.log('✅ FileStorage initialized');
    },

    /**
     * Load all data from server (Async but fills dataStore)
     */
    async loadAllFromServer() {
        console.log('🌐 Fetching data from server...');
        const token = AuthManager.getCsrfToken();
        const headers = {};
        if (token) {
            headers['X-SICAF-CSRF'] = token;
        }

        const isStudent = typeof AuthManager !== 'undefined' && AuthManager.isStudent();
        const canReadUsers = typeof AuthManager !== 'undefined' && (AuthManager.isAdmin() || AuthManager.isCoordinator());
        if (isStudent) {
            // SEGURIDAD: Aislar sesión de alumno; purgar datos residuales de administración en BrowserState
            try {
                const myStudentId = AuthManager.getStudentId();
                if (myStudentId) {
                    // Fetch own student profile directly from API to ensure it is always cached
                    try {
                        const sRes = await fetch(`/api/students/${myStudentId}`, { headers });
                        if (sRes.ok) {
                            const sData = await sRes.json();
                            if (sData.success && sData.student) {
                                BrowserState.setItem('ecpv_students', JSON.stringify([sData.student]));
                                this.dataStore.students = [sData.student];
                            }
                        }
                    } catch(err) {
                        console.warn('Error fetching own student data in loadAllFromServer:', err);
                    }
                } else {
                    const stored = BrowserState.getItem('ecpv_students');
                    if (stored) {
                        const parsed = JSON.parse(stored);
                        if (Array.isArray(parsed)) {
                            const selfOnly = parsed.filter(s => s.id === myStudentId);
                            BrowserState.setItem('ecpv_students', JSON.stringify(selfOnly));
                            this.dataStore.students = selfOnly;
                        }
                    }
                }
                BrowserState.removeItem('ecpv_leads');
                BrowserState.removeItem('ecpv_users_db');
                BrowserState.removeItem('ecpv_payroll_history');
                BrowserState.removeItem('ecpv_professor_docs');
            } catch(e) {}
        }

        // 'justificantes' se excluye de este bloqueo: los alumnos ahora suben y ven sus
        // propios justificantes médicos (JustificantesView filtra por su studentId al
        // renderizar), igual que ya hacían los profesores con los suyos.
        const sensitiveStaffKeys = ['students', 'leads', 'users', 'payrollHistory', 'professorDocs'];

        const promises = Object.entries(this.fileMapping).map(async ([key, filename]) => {
            if (key === 'users' && !canReadUsers) return;
            if (isStudent && sensitiveStaffKeys.includes(key)) {
                return; // Bloquear descarga de colecciones confidenciales para alumnos
            }
            // Contacts: only the figures at login; the list loads with its view, one year at a time.
            if (key === 'leads') return this.loadLeadsSummary();

            try {
                let url = `/data/${filename}?t=${new Date().getTime()}`;
                let isApi = false;
                const warehousePaths={inventory:'inventory',warehouseRequests:'requests',purchaseOrders:'orders',projects:'projects'};
                // Classes: only the current academic year at login; other years on demand.
                if (key === 'classes') {
                    // Classes: only the current month at login; everything else when a screen shows it.
                    const month = this.classDay(new Date()).slice(0, 7);
                    url = `data/classes.json?${new URLSearchParams(this.monthRange(month, month))}`;
                    this._classSlices = new Set(['m:' + month]);
                    this._classSliceLoads = {};
                    this._classSummaries = {};
                    this._classYearLists = {};
                    this.loadClassYears();
                }
                if(warehousePaths[key]) { url='/api/warehouse/'+warehousePaths[key];isApi=true; }

                // Si hay un token y es una clave persistida en BD, usar la API REST
                if (token) {
                    if (key === 'professors') { url = '/api/professors'; isApi = true; }
                    else if (key === 'formations') { url = '/api/formations'; isApi = true; }
                    else if (key === 'subjects') { url = '/api/subjects'; isApi = true; }
                    else if (key === 'students') { url = '/api/students'; isApi = true; }
                    else if (key === 'leads') { url = '/api/leads'; isApi = true; }
                    else if (key === 'users') { url = '/api/users'; isApi = true; }
                }

                const response = await fetch(url, { headers });
                if (response.ok) {
                    const resJson = await response.json();
                    const data = isApi ? resJson[key] : resJson;

                    this.dataStore[key] = data;
                    this.rememberServerCopy(key, data);
                    if (this.COMMUNICATION_KEYS.includes(key)) this._commMore[key] = this.moreFolders(response);

                    // Update BrowserState to match server
                    const storageKey = Object.keys(this.keyMapping).find(k => this.keyMapping[k] === key);
                    if (storageKey) {
                        BrowserState.setItem(storageKey, JSON.stringify(data));
                    }
                }
            } catch (e) {
                // Ignore 404s or errors, keep default/local
            }
        });

        await Promise.all(promises);
        console.log('✨ Data synchronized from server');

        // Refresh UI if App is ready
        if (typeof App !== 'undefined' && App.refreshCurrentView) {
            App.refreshCurrentView();
        } else if (typeof App !== 'undefined' && App.renderLogin && !AuthManager.isLoggedIn()) {
            // If on login screen
        }

        // Special case: Update AuthManager users if they changed
        if (typeof AuthManager !== 'undefined') {
            if (!canReadUsers) {
                try {
                    const contactsRes = await fetch('/api/contacts', { headers });
                    if (contactsRes.ok) {
                        const cData = await contactsRes.json();
                        if (cData.success && Array.isArray(cData.contacts)) {
                            AuthManager.users = cData.contacts;
                        }
                    }
                } catch (err) {
                    console.warn('Error fetching contacts for student:', err);
                }
            } else {
                const savedUsers = this.getItem('ecpv_users_db');
                if (savedUsers) {
                    AuthManager.users = JSON.parse(savedUsers);
                }
            }
        }
    },

    // Saves requested and saves finished per list. While they differ, a download
    // may predate the change and would put it back on screen until the next poll.
    _commWrites: {},
    _commSettled: {},

    /** Server copy of messages/notifications, or null if it failed or may be older than a local change. */
    async fetchCommunicationList(key) {
        const writes = this._commWrites[key] || 0;
        const busy = () => (this._commWrites[key] || 0) !== writes || writes !== (this._commSettled[key] || 0);
        if (busy()) return null;
        const token = typeof AuthManager !== 'undefined' ? AuthManager.getCsrfToken() : null;
        const response = await fetch(`data/${this.fileMapping[key]}?t=${Date.now()}`, { headers: token ? { 'X-SICAF-CSRF': token } : {} });
        if (!response.ok) return null;
        let list = await response.json();
        if (busy()) return null;
        const more = this.moreFolders(response);
        if (this._commOlder[key]?.size && Array.isArray(this.dataStore[key])) {
            // Older pages were opened with "Cargar más": keep everything already shown
            // (with no save pending it matches the server) and the deeper cursors.
            const fresh = new Set(list.map(item => String(item.id)));
            list = list.concat(this.dataStore[key].filter(item => !fresh.has(String(item.id))));
            for (const [folder, cursor] of this._commMore[key] || []) {
                const next = more.get(folder);
                if (next && this.cursorTime(cursor) < this.cursorTime(next)) more.set(folder, cursor);
            }
        } else {
            this._commEnded[key] = new Set();
        }
        this._commMore[key] = more;
        this.rememberServerCopy(key, list);
        return list;
    },

    // ---- Older pages ("Cargar más") ----
    _commMore: {},
    _commEnded: {},
    _commOlder: {},

    /** folder → where its last page ended ({date, id}), from "folder=<date>~<id>,…". */
    moreFolders(response) {
        const more = new Map();
        for (const part of (response.headers?.get?.('X-SICAF-More') || '').split(',').filter(Boolean)) {
            const [folder, value = ''] = part.split('=');
            const text = decodeURIComponent(value), cut = text.indexOf('~');
            if (cut > 0) more.set(folder, { date: text.slice(0, cut), id: text.slice(cut + 1) });
        }
        return more;
    },

    cursorTime(cursor) {
        return new Date(cursor.date).getTime();
    },

    hasMoreCommunications(key, folder = key) {
        return !!this._commMore[key]?.has(folder) && !this._commEnded[key]?.has(folder);
    },

    /**
     * What a list shows: while older pages remain, only down to where the last page ended.
     * Older unread items are loaded for the counters and «Marcar leídas», but appear in
     * the list when the reader gets there, so it never has gaps.
     */
    visibleCommunications(key, folder, items) {
        if (!this.hasMoreCommunications(key, folder)) return items;
        const cursor = this._commMore[key].get(folder), time = this.cursorTime(cursor);
        return items.filter(item => {
            const date = new Date(item.date).getTime();
            return date > time || (date === time && String(item.id) >= cursor.id);
        });
    },

    /** Adds the next older page of a folder, continuing where the last page ended. */
    async loadMoreCommunications(key, folder) {
        const cursor = this._commMore[key]?.get(folder);
        if (!cursor) return false;
        const params = new URLSearchParams({ before: cursor.date, beforeId: cursor.id });
        if (key === 'messages') params.set('folder', folder);
        try {
            const token = typeof AuthManager !== 'undefined' ? AuthManager.getCsrfToken() : null;
            const response = await fetch(`data/${this.fileMapping[key]}?${params}`, { headers: token ? { 'X-SICAF-CSRF': token } : {} });
            if (!response.ok) return false;
            const page = await response.json();
            const next = this.moreFolders(response).get(folder);
            if (next) this._commMore[key].set(folder, next);
            else (this._commEnded[key] ||= new Set()).add(folder);
            const current = Array.isArray(this.dataStore[key]) ? this.dataStore[key] : [];
            const known = new Set(current.map(item => String(item.id)));
            const added = page.filter(item => !known.has(String(item.id)));
            const older = this._commOlder[key] ||= new Set();
            const copy = this._serverCopy[key] ||= new Map();
            added.forEach(item => { older.add(String(item.id)); copy.set(String(item.id), JSON.stringify(item)); });
            this.dataStore[key] = current.concat(added);
            BrowserState.setItem(key === 'messages' ? 'ecpv_messages' : 'ecpv_notifications', JSON.stringify(this.dataStore[key]));
            return true;
        } catch (e) {
            return false;
        }
    },

    /** "Eliminar todas": hidden on the server too, including notices never downloaded. */
    async hideAllNotifications() {
        const ok = await this.sendJson('POST', '/api/notifications/hide-all', {}, 'notifications.json');
        if (!ok) return false;
        const list = (this.dataStore.notifications || []).map(n => ({ ...n, hidden: true }));
        this.dataStore.notifications = list;
        this.rememberServerCopy('notifications', list);
        this._commEnded.notifications = new Set(['notifications']);
        BrowserState.setItem('ecpv_notifications', JSON.stringify(list));
        this.forgetCommunicationsState('notifications');
        return true;
    },

    async syncNotifications() {
        try {
            const notifications = await this.fetchCommunicationList('notifications');
            if (notifications) {
                this.dataStore.notifications = notifications;
                BrowserState.setItem('ecpv_notifications', JSON.stringify(notifications));
                
                if (typeof App !== 'undefined' && App.setupHeaderInfo) {
                    App.setupHeaderInfo();
                }
                return true;
            }
        } catch (e) {
            // Silently ignore sync errors
        }
        return false;
    },

    async syncMessages() {
        try {
            const messages = await this.fetchCommunicationList('messages');
            if (messages) {
                const oldHash = JSON.stringify(this.dataStore.messages || []);
                const newHash = JSON.stringify(messages || []);
                if (oldHash !== newHash) {
                    this.dataStore.messages = messages;
                    BrowserState.setItem('ecpv_messages', JSON.stringify(messages));
                    
                    if (typeof App !== 'undefined' && App.setupHeaderInfo) {
                        App.setupHeaderInfo();
                    }
                    if (typeof App !== 'undefined' && App.currentView === 'mensajes' && typeof MessagesView !== 'undefined') {
                        if (!MessagesView.isComposing) {
                            MessagesView.refresh(false);
                        }
                    }
                    if (typeof DashboardView !== 'undefined' && DashboardView.updateMessagesWidget) {
                        DashboardView.updateMessagesWidget();
                    }
                }
                return true;
            }
        } catch (e) {
            // Silently ignore sync errors
        }
        return false;
    },

    // ---- Change detection for background polling ----
    // The server returns a small fingerprint per list; full inboxes are only
    // downloaded when it moves. Fingerprints are tied to the auth epoch so a
    // logout/login never reuses another session's state.
    _communicationsState: null,

    _knownCommunications() {
        const epoch = typeof AuthManager !== 'undefined' ? AuthManager._epoch : 0;
        if (!this._communicationsState || this._communicationsState.epoch !== epoch) {
            this._communicationsState = { epoch };
        }
        return this._communicationsState;
    },

    async fetchCommunicationsState() {
        try {
            const token = typeof AuthManager !== 'undefined' ? AuthManager.getCsrfToken() : null;
            const headers = token ? { 'X-SICAF-CSRF': token } : {};
            const response = await fetch('/api/communications/state', { headers, cache: 'no-store' });
            if (!response.ok) return null;
            const state = await response.json();
            return state?.success ? state : null;
        } catch (e) {
            return null;
        }
    },

    // Without a fingerprint (old server, network error) assume it changed: full sync as before.
    communicationsChanged(state, kind) {
        return !state || this._knownCommunications()[kind] !== state[kind];
    },

    markCommunicationsSynced(kind, state) {
        if (state) this._knownCommunications()[kind] = state[kind];
    },

    forgetCommunicationsState(kind) {
        delete this._knownCommunications()[kind];
    },

    async syncCommunicationsIfChanged({ messages = true, notifications = true } = {}) {
        // Read the fingerprint BEFORE the lists: a change landing in between
        // costs one extra download next time, never a missed update.
        const state = await this.fetchCommunicationsState();
        if (messages && this.communicationsChanged(state, 'messages') && await this.syncMessages()) {
            this.markCommunicationsSynced('messages', state);
        }
        if (notifications && this.communicationsChanged(state, 'notifications') && await this.syncNotifications()) {
            this.markCommunicationsSynced('notifications', state);
        }
    },

    /**
     * Get value by path (e.g., 'settings.currentUser')
     */
    getByPath(path) {
        const parts = path.split('.');
        let value = this.dataStore;
        for (const part of parts) {
            value = value?.[part];
        }
        return value;
    },

    /**
     * Set value by path
     */
    setByPath(path, value) {
        const parts = path.split('.');
        let obj = this.dataStore;
        for (let i = 0; i < parts.length - 1; i++) {
            obj = obj[parts[i]];
        }
        obj[parts[parts.length - 1]] = value;
    },

    /**
     * Read data (SYNCHRONOUS)
     */
    read(key) {
        const path = this.keyMapping[key];
        if (!path) {
            console.warn(`No mapping for key: ${key}`);
            return null;
        }
        return this.getByPath(path);
    },

    // Documents are not cached or reported as saved until scanning and persistence succeed.
    async writeConfirmed(key, data) {
        if (!['ecpv_professor_docs', 'ecpv_justificantes', 'ecpv_payroll_history'].includes(key)) throw new Error('Guardado confirmado no disponible para este módulo.');
        const internalPath = this.keyMapping[key], filename = this.fileMapping[internalPath];
        const session = AuthManager.getCsrfToken();
        if (!session || !filename) throw new Error('Inicia sesión de nuevo antes de guardar.');
        const response = await fetch('/api/documents/sync', {method:'POST', headers:{'Content-Type':'application/json','X-SICAF-CSRF':session}, body:JSON.stringify({filename,data})});
        const result = await response.json().catch(() => null);
        if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudo guardar o analizar el archivo. Conserva el documento y vuelve a intentarlo.');
        if (AuthManager.getCsrfToken() !== session) throw new Error('La sesión cambió durante la subida. Vuelve a cargar la ficha.');
        this.setByPath(internalPath, data);
        BrowserState.setItem(key, JSON.stringify(data));
        return true;
    },

    /**
     * Write data (SYNCHRONOUS)
     */
    write(key, data) {
        if (['ecpv_inventory','ecpv_warehouse_requests','ecpv_purchase_orders','ecpv_warehouse_projects'].includes(key)) {
            throw new Error('El almacén se guarda mediante operaciones confirmadas, no colecciones completas.');
        }
        if (key === 'ecpv_users_db') {
            throw new Error('El guardado masivo de usuarios está retirado. Usa la API de usuarios.');
        }
        const path = this.keyMapping[key];
        if (!path) {
            console.warn(`No mapping for key: ${key}`);
            return false;
        }

        this.setByPath(path, data);

        // Also save to BrowserState as backup
        try {
            const stringValue = (typeof data === 'string') ? data : JSON.stringify(data);
            BrowserState.setItem(key, stringValue);
        } catch (e) {
            console.warn(`Failed to save to BrowserState:`, e);
        }

        // Sync to server (Fire and forget - don't await)
        this.saveToServer(path);

        return true;
    },

    /**
     * Remove data (SYNCHRONOUS)
     */
    remove(key) {
        const path = this.keyMapping[key];
        if (!path) return false;

        // Determine default empty value
        let emptyValue = [];
        if (key === 'ecpv_calendar_filters') {
            emptyValue = {};
        } else if (key === 'ecpv_current_user') {
            emptyValue = null;
        }

        this.setByPath(path, emptyValue);

        try {
            BrowserState.removeItem(key);
        } catch (e) {
            console.warn(`Failed to remove from BrowserState:`, e);
        }

        // Sync to server
        this.saveToServer(path);

        return true;
    },

    /** Contactos CRM figures (panel, profile): counts, top interests, latest pending, last months. */
    async loadLeadsSummary() {
        try {
            const response = await fetch('/api/leads/summary', { cache: 'no-store' });
            if (!response.ok) return;
            const body = await response.json();
            if (body.success) this.dataStore.leadsSummary = body;
        } catch (e) {
            // Without the figures the panels show zeros, as before for people without access.
        }
    },

    // Programming collections are saved as explicit change sets
    // (server/security/programming-writes.js), never as whole collections.
    CHANGE_SET_KEYS: ['formations', 'subjects', 'classes'],
    // Messages and notices: only items that differ from the server copy are sent.
    COMMUNICATION_KEYS: ['messages', 'notifications'],
    // Browser-only data: per-user preferences and caches filled from API responses.
    LOCAL_ONLY_KEYS: ['calendarFilters', 'students', 'currentUser'],
    _serverCopy: {},
    _changeQueue: Promise.resolve(),
    _queuedChangeSets: new Set(),
    _saveQueues: {},

    /**
     * One save at a time per collection. Saves requested while one is running
     * collapse into a single follow-up that sends the latest data, so "delete
     * all" or quick clicks never race each other on the server.
     */
    queueSave(key, send) {
        const queue = this._saveQueues[key] || (this._saveQueues[key] = { running: Promise.resolve(), waiting: false });
        if (queue.waiting) return queue.running;
        queue.waiting = true;
        queue.running = queue.running.then(() => { queue.waiting = false; return send(); });
        return queue.running;
    },

    // ---- Classes: only what is on screen ----
    // Loaded slices: 'm:YYYY-MM' (every formation, one month), 'f:<id>:<year>'
    // (one formation, one academic year from September) and 'p:<id>:<year>'
    // (one professor). Summaries per formation/subject come from the server.
    _classSlices: new Set(),
    _classSliceLoads: {},
    _classSummaries: {},
    _classYearLists: {},
    classYears: [],

    classDay(date) {
        if (typeof date === 'string') return date.slice(0, 10);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },

    classBlock(date) {
        const [y, m] = this.classDay(date).split('-').map(Number);
        return m >= 9 ? y : y - 1;
    },

    classBlockRange(block) {
        return { from: `${block}-09-01`, to: `${block + 1}-08-31` };
    },

    classBlocksBetween(from, to) {
        const first = this.classBlock(from), last = this.classBlock(to || from), blocks = [];
        for (let block = first; block <= last; block++) blocks.push(block);
        return blocks;
    },

    classMonthsBetween(from, to) {
        let [y, m] = this.classDay(from).split('-').map(Number);
        const [ly, lm] = this.classDay(to || from).split('-').map(Number), months = [];
        while (y < ly || (y === ly && m <= lm)) {
            months.push(`${y}-${String(m).padStart(2, '0')}`);
            if (++m > 12) { m = 1; y++; }
        }
        return months;
    },

    monthRange(first, last) {
        const [y, m] = last.split('-').map(Number);
        return { from: `${first}-01`, to: `${last}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}` };
    },

    // Accepts (from, to) for every formation, or a scope {from, to, formationId | professorId}.
    classScope(scopeOrFrom, to) {
        const scope = scopeOrFrom && typeof scopeOrFrom === 'object' && !(scopeOrFrom instanceof Date) ? scopeOrFrom : { from: scopeOrFrom, to };
        return { ...scope, from: this.classDay(scope.from), to: this.classDay(scope.to || scope.from) };
    },

    // True when every class the scope needs is already in memory.
    classScopeLoaded(scope) {
        if (this.classMonthsBetween(scope.from, scope.to).every(month => this._classSlices.has('m:' + month))) return true;
        const owner = scope.formationId ? 'f:' + scope.formationId : scope.professorId ? 'p:' + scope.professorId : null;
        return !!owner && this.classBlocksBetween(scope.from, scope.to).every(block => this._classSlices.has(`${owner}:${block}`));
    },

    // What is still missing for a scope, as the requests that would load it.
    // Slices already on their way count as present (ensureClasses waits for them).
    missingClassRequests(scope) {
        const have = slice => this._classSlices.has(slice) || !!this._classSliceLoads[slice];
        const months = this.classMonthsBetween(scope.from, scope.to);
        if (months.every(month => have('m:' + month))) return [];
        const owner = scope.formationId ? ['f', 'formationId', scope.formationId] : scope.professorId ? ['p', 'professorId', scope.professorId] : null;
        if (owner) {
            return this.classBlocksBetween(scope.from, scope.to)
                .filter(block => !have(`${owner[0]}:${owner[2]}:${block}`))
                .map(block => ({ slices: [`${owner[0]}:${owner[2]}:${block}`], params: { ...this.classBlockRange(block), [owner[1]]: owner[2] } }));
        }
        const missing = months.filter(month => !have('m:' + month));
        const span = this.classMonthsBetween(missing[0] + '-01', missing[missing.length - 1] + '-01').filter(month => !have('m:' + month));
        return [{ slices: span.map(month => 'm:' + month), params: this.monthRange(span[0], span[span.length - 1]) }];
    },

    /** Academic years that have classes (optionally for some formations), for year selectors. */
    async loadClassYears(formationIds = null) {
        const key = formationIds ? formationIds.slice().sort().join(',') : '';
        try {
            const token = AuthManager.getCsrfToken();
            const query = key ? '?' + new URLSearchParams({ formationIds: key }) : '';
            const response = await fetch('/api/classes/years' + query, { headers: token ? { 'X-SICAF-CSRF': token } : {} });
            const body = response.ok ? await response.json() : null;
            if (Array.isArray(body?.years)) {
                this._classYearLists[key] = body.years;
                if (!key) this.classYears = body.years;
                return true;
            }
        } catch (e) { /* selectors fall back to the loaded years */ }
        return false;
    },

    /** Year list for some formations: returns it when known, otherwise loads it and calls onLoaded. */
    needClassYears(formationIds, onLoaded) {
        const key = formationIds && formationIds.length ? formationIds.slice().sort().join(',') : '';
        if (this._classYearLists[key]) return this._classYearLists[key];
        this.loadClassYears(key ? formationIds : null).then(ok => { if (ok && typeof onLoaded === 'function') onLoaded(); });
        return null;
    },

    /** Loads whatever the scope needs that is not in memory yet. */
    async ensureClasses(scopeOrFrom, to) {
        const scope = this.classScope(scopeOrFrom, to);
        const before = new Set(this._classSlices);
        // Loads already on their way for this scope are awaited, never repeated.
        const waiting = Object.values(this._classSliceLoads);
        const requests = this.missingClassRequests(scope);
        requests.forEach(request => {
            const load = this.loadClassSlice(request, new URLSearchParams(request.params).toString());
            request.slices.forEach(slice => { this._classSliceLoads[slice] = load; });
        });
        await Promise.all([...new Set([...waiting, ...requests.map(request => this._classSliceLoads[request.slices[0]])])]);
        // True only when something new arrived and the scope is complete (a failed load is retried on the next render).
        const grew = this._classSlices.size > before.size;
        return grew && this.classScopeLoaded(scope);
    },

    async loadClassSlice(request, key) {
        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch(`/data/classes.json?${key}`, { headers: token ? { 'X-SICAF-CSRF': token } : {} });
            if (!response.ok) throw new Error('HTTP ' + response.status);
            const rows = await response.json();
            if (!Array.isArray(rows)) throw new Error('Respuesta no válida');
            const known = new Set((this.dataStore.classes || []).map(c => String(c.id)));
            this.dataStore.classes = [...(this.dataStore.classes || []), ...rows.filter(c => !known.has(String(c.id)))];
            const copy = this._serverCopy.classes || new Map();
            rows.forEach(c => { if (!copy.has(String(c.id))) copy.set(String(c.id), JSON.stringify(c)); });
            this._serverCopy.classes = copy;
            BrowserState.setItem('ecpv_classes', JSON.stringify(this.dataStore.classes));
            request.slices.forEach(slice => this._classSlices.add(slice));
        } catch (e) {
            if (typeof Utils !== 'undefined') Utils.showToast(`No se pudieron cargar las clases (${request.params.from} a ${request.params.to}).`, 'error');
        } finally {
            request.slices.forEach(slice => { delete this._classSliceLoads[slice]; });
        }
    },

    /**
     * For render code: true when the scope is already in memory. Otherwise it
     * starts loading and calls onLoaded once, so the view can draw again.
     * needClasses(from, to, onLoaded) covers every formation; needClasses(scope, onLoaded)
     * takes {from, to, formationId | professorId}.
     */
    needClasses(scopeOrFrom, toOrCallback, onLoaded) {
        const scoped = scopeOrFrom && typeof scopeOrFrom === 'object' && !(scopeOrFrom instanceof Date);
        const scope = scoped ? this.classScope(scopeOrFrom) : this.classScope(scopeOrFrom, toOrCallback);
        const callback = scoped ? toOrCallback : onLoaded;
        if (this.classScopeLoaded(scope)) return true;
        this.ensureClasses(scope).then(loaded => { if (loaded && typeof callback === 'function') callback(); });
        return false;
    },

    /**
     * Class count and hours grouped by formation or subject, computed on the server.
     * Returns the rows when known; otherwise loads them and calls onLoaded.
     */
    needClassSummary(query, onLoaded) {
        const key = new URLSearchParams(Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString();
        if (this._classSummaries[key]) return this._classSummaries[key];
        if (!this._classSummaries['loading:' + key]) {
            this._classSummaries['loading:' + key] = true;
            const token = AuthManager.getCsrfToken();
            fetch('/api/classes/summary?' + key, { headers: token ? { 'X-SICAF-CSRF': token } : {} })
                .then(response => response.ok ? response.json() : null)
                .then(body => {
                    delete this._classSummaries['loading:' + key];
                    if (!Array.isArray(body?.rows)) return;
                    this._classSummaries[key] = body.rows;
                    if (typeof onLoaded === 'function') onLoaded();
                })
                .catch(() => { delete this._classSummaries['loading:' + key]; });
        }
        return null;
    },

    /**
     * Remember what the server last confirmed, to send only real changes.
     */
    rememberServerCopy(key, items) {
        if (![...this.CHANGE_SET_KEYS, ...this.COMMUNICATION_KEYS].includes(key) || !Array.isArray(items)) return;
        this._serverCopy[key] = new Map(items.map(item => [String(item.id), JSON.stringify(item)]));
    },

    /**
     * Save data to server (ASYNC - Fire & Forget)
     */
    async saveToServer(internalPath) {
        const rootKey = internalPath.split('.')[0];
        const filename = this.fileMapping[rootKey];

        if (!filename || this.LOCAL_ONLY_KEYS.includes(rootKey)) return;
        if (this.CHANGE_SET_KEYS.includes(rootKey)) return this.queueChangeSet(rootKey);
        if (rootKey === 'studyBlocks') {
            // Reading the plan seeds default blocks; only managers persist them.
            if (!AuthManager.canManageModule('plan-estudios')) return;
            return this.queueSave(rootKey, () => this.sendJson('PUT', '/api/programming/study-blocks', { blocks: this.dataStore.studyBlocks }, filename));
        }
        if (rootKey === 'zones') {
            if (!AuthManager.isAdmin()) return;
            return this.queueSave(rootKey, () => this.sendJson('PUT', '/api/floorplan-zones', { zones: this.dataStore.zones }, filename));
        }

        // Communications and private documents keep their scoped sync handlers.
        if (this.COMMUNICATION_KEYS.includes(rootKey)) {
            this._commWrites[rootKey] = (this._commWrites[rootKey] || 0) + 1;
            return this.queueSave(rootKey, () => this.sendCommunicationChanges(rootKey));
        }
        const saveUrl = ['professorDocs', 'payrollHistory', 'justificantes'].includes(rootKey) ? '/api/documents/sync' : null;
        if (!saveUrl) {
            // No generic collection writer exists: each module must use its own API.
            console.error(`No server writer for ${filename}; change not saved.`);
            if (typeof Utils !== 'undefined') Utils.showToast(`Este cambio no se puede guardar en el servidor (${filename}).`, 'error');
            return false;
        }
        return this.queueSave(rootKey, () => this.sendJson('POST', saveUrl, { filename, data: JSON.stringify(this.dataStore[rootKey], null, 2) }, filename));
    },

    /**
     * Marking one message as read sends that message, not the whole inbox.
     * Without a server copy (data from the browser cache) the full list is
     * sent as before; the server never deletes what is left out.
     */
    async sendCommunicationChanges(key) {
        // This send carries the latest data, so it covers every change requested so far.
        const writes = this._commWrites[key] || 0;
        try {
            return await this.sendCommunicationDiff(key);
        } finally {
            this._commSettled[key] = Math.max(this._commSettled[key] || 0, writes);
        }
    },

    async sendCommunicationDiff(key) {
        const items = Array.isArray(this.dataStore[key]) ? this.dataStore[key] : [];
        const known = this._serverCopy[key];
        const current = new Map(items.map(item => [String(item.id), JSON.stringify(item)]));
        const changed = known ? items.filter(item => known.get(String(item.id)) !== current.get(String(item.id))) : items;
        if (!changed.length) return true;

        this.forgetCommunicationsState(key);
        const ok = await this.sendJson('POST', '/api/communications/sync', { filename: this.fileMapping[key], data: changed }, this.fileMapping[key]);
        if (ok) {
            const copy = known || new Map();
            changed.forEach(item => copy.set(String(item.id), current.get(String(item.id))));
            this._serverCopy[key] = copy;
        }
        return ok;
    },

    /**
     * One queue for all programming collections: a new formation reaches the
     * server before its subjects and classes, and repeated saves collapse.
     */
    queueChangeSet(key) {
        if (this._queuedChangeSets.has(key)) return this._changeQueue;
        this._queuedChangeSets.add(key);
        this._changeQueue = this._changeQueue.then(() => {
            this._queuedChangeSets.delete(key);
            return this.sendChangeSet(key);
        });
        return this._changeQueue;
    },

    async sendChangeSet(key) {
        const items = Array.isArray(this.dataStore[key]) ? this.dataStore[key] : [];
        const known = this._serverCopy[key];
        const current = new Map(items.map(item => [String(item.id), JSON.stringify(item)]));
        const upsert = items.filter(item => known?.get(String(item.id)) !== current.get(String(item.id)));
        // Without a server copy nothing is deleted: absence never implies removal.
        const remove = known ? [...known.keys()].filter(id => !current.has(id)) : [];
        if (!upsert.length && !remove.length) return true;

        const ok = await this.sendJson('POST', `/api/programming/${key}`, { upsert, delete: remove }, this.fileMapping[key]);
        if (ok) {
            const copy = known || new Map();
            upsert.forEach(item => copy.set(String(item.id), current.get(String(item.id))));
            remove.forEach(id => copy.delete(id));
            this._serverCopy[key] = copy;
            // Counts and year lists may have changed.
            this._classSummaries = {};
            this._classYearLists = {};
        } else {
            await this.reloadCollection(key);
        }
        return ok;
    },

    /**
     * After a rejected save, show the server's data again instead of the unsaved edit.
     */
    async reloadCollection(key) {
        const sources = { formations: ['/api/formations', 'formations'], subjects: ['/api/subjects', 'subjects'], classes: ['data/classes.json', null] };
        const [url, field] = sources[key];
        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch(url, { headers: token ? { 'X-SICAF-CSRF': token } : {}, cache: 'no-store' });
            if (!response.ok) return;
            const body = await response.json();
            const data = field ? body[field] : body;
            if (!Array.isArray(data)) return;
            this.dataStore[key] = data;
            this.rememberServerCopy(key, data);
            const storageKey = Object.keys(this.keyMapping).find(k => this.keyMapping[k] === key);
            if (storageKey) BrowserState.setItem(storageKey, JSON.stringify(data));
            if (typeof App !== 'undefined' && App.refreshCurrentView) App.refreshCurrentView();
        } catch (e) {
            // Keep the local copy; the error was already reported.
        }
    },

    async sendJson(method, url, body, filename) {
        const token = AuthManager.getCsrfToken();
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
            headers['X-SICAF-CSRF'] = token;
        }

        try {
            const response = await fetch(url, { method, headers, body: JSON.stringify(body) });

            // Parse response
            const result = await response.json().catch(() => null);

            // A blocked/empty response is a failed save, not corrupt local JSON.
            if (!response.ok || !result?.success) {
                const errorMsg = result?.error || `No se pudo guardar: respuesta del servidor HTTP ${response.status}`;
                console.error(`Failed to sync ${filename}:`, errorMsg);
                if (response.status === 401 || (errorMsg && errorMsg.toLowerCase().includes('token'))) {
                    if (typeof Swal !== 'undefined') {
                        SafeHTML.fire({
                            title: 'Sesión Expirada',
                            text: 'Tu sesión de acceso ha expirado. Por favor, inicia sesión de nuevo para poder guardar y sincronizar los datos.',
                            icon: 'warning',
                            confirmButtonColor: '#0F766E',
                            confirmButtonText: 'Iniciar sesión'
                        }).then(() => {
                            if (typeof AuthManager !== 'undefined') {
                                AuthManager.logout();
                            } else {
                                BrowserState.removeItem('ecpv_jwt_token');
                                window.location.reload();
                            }
                        });
                    } else {
                        alert('Tu sesión de acceso ha expirado. Por favor, inicia sesión de nuevo.');
                        if (typeof AuthManager !== 'undefined') {
                            AuthManager.logout();
                        } else {
                            BrowserState.removeItem('ecpv_jwt_token');
                            window.location.reload();
                        }
                    }
                } else if (typeof Utils !== 'undefined') {
                    Utils.showToast(`Error de sincronización (${filename}): ${errorMsg}`, 'error');
                }
                return false;
            }
            console.log(`✅ Synced ${filename} to server`);
            return true;
        } catch (e) {
            console.error(`Failed to sync ${filename} to server:`, e);
            if (typeof Utils !== 'undefined') {
                const offline = e.message.includes('Failed to fetch');
                Utils.showToast(offline ? `Sin conexión con el servidor: no se ha guardado (${filename}).` : `Error crítico (${filename}): ${e.message}`, 'error');
            }
            return false;
        }
    },



    /**
     * Get item (wrapper for read) - SYNCHRONOUS
     */
    getItem(key) {
        const data = this.read(key);
        if (data === null || data === undefined) return null;
        return JSON.stringify(data);
    },

    /**
     * Set item (wrapper for write) - SYNCHRONOUS
     */
    setItem(key, value) {
        try {
            const data = JSON.parse(value);
            if (!this.write(key, data)) {
                BrowserState.setItem(key, value);
            }
        } catch (error) {
            // If it's a plain string, try writing directly or save in BrowserState
            if (this.keyMapping[key]) {
                this.write(key, value);
            } else {
                BrowserState.setItem(key, value);
            }
        }
    },

    /**
     * Remove item - SYNCHRONOUS
     */
    removeItem(key) {
        this.remove(key);
    },


};

// Make FileStorage globally available
window.FileStorage = FileStorage;

// App initializes storage only after server-side session validation.
